import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Api } from '../adapters/Api';
import type { BotSettings, BotStatus, ModuleKey, QueueSnapshotRow } from '../domain/types';
import { defaultSettings } from '../domain/types';
import { useI18n, type TranslationKey } from '../i18n';
import { EmptyState, Metric, Panel, StatusPill } from './components';
import { ActionSettingsPanel } from './ActionSettingsPanel';
import { OperationModal, type OperationModalTone } from './OperationModal';
import { useClientConnected } from './connection';
import { describeStatus, formatRelative, stateLabel } from './status';
import { IntelligencePanel } from './IntelligencePanel';

// Dashboard con el FORMATO DEL HOSTED (apps/web DashboardPage): rejilla de tres
// columnas (acciones/ajustes · captura · verificaciones), mismos nombres de
// acciones y feedback por OperationModal. La semántica del plano de control es
// la del descargable: desired run/stop + modules; el cliente sondea y reporta.

type ActionKey = 'auto_bot' | 'collect' | 'train_troops' | 'auto_hunt' | 'stop';

// 0.5.8: fuera los botones por-módulo (Collect/Train/Auto Hunt) — activar el
// módulo en su sección de ajustes + Auto Bot equivale a pulsarlos, y el hueco
// de UI se reserva para las features nuevas. Las claves i18n dashboard.collect/
// trainTroops/autoHunt siguen vivas (status.ts las usa para traducir actividad).
const actions: { key: ActionKey; labelKey: TranslationKey; tone: 'primary' | 'danger' }[] = [
  { key: 'auto_bot', labelKey: 'dashboard.autoBot', tone: 'primary' },
  { key: 'stop', labelKey: 'dashboard.stopBot', tone: 'danger' }
];

const CAPTURE_FRESH_MS = 5 * 60_000;   // captura más vieja → vista simulada
const OPERATION_TIMEOUT_MS = 60_000;   // sin confirmación del cliente → aviso
const CAPTURE_TIMEOUT_MS = 45_000;     // captura bajo demanda sin respuesta → aviso
const TUTORIAL_KEY = 'wb-dl:tutorial-done';

interface Operation {
  kind: 'start' | 'stop';
  actionLabel: string;
  phase: 'confirm' | 'working' | 'success' | 'timeout';
  startedAt: number;
  pendingAction?: ActionKey;
}

// Petición de captura bajo demanda (p5): se resuelve cuando capture_at CAMBIA
// respecto al valor que había al pedirla (robusto a desfases de reloj del PC).
interface CaptureRequest {
  startedAt: number;
  prevCaptureAt: string | null;
  phase: 'pending' | 'timeout';
}

export function Dashboard({ api, instanceId, onBack }: { api: Api; instanceId?: string; onBack?: () => void }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const statusQuery = useQuery({ queryKey: ['botStatus', instanceId ?? 'default'], queryFn: () => api.getBotStatus(instanceId), refetchInterval: 2000 });

  const [modules, setModules] = useState<Record<ModuleKey, boolean>>({ collect: true, train_troops: false, auto_hunt: false });
  const [settings, setSettings] = useState<BotSettings>(defaultSettings());
  const [synced, setSynced] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [operation, setOperation] = useState<Operation | null>(null);
  const [captureReq, setCaptureReq] = useState<CaptureRequest | null>(null);
  const [tutorialOpen, setTutorialOpen] = useState(() => localStorage.getItem(TUTORIAL_KEY) !== '1');
  const [langWarnDismissed, setLangWarnDismissed] = useState(false);
  const saveTimer = useRef<number | null>(null);
  const dirty = useRef(false);

  const status = statusQuery.data;
  const running = !!status?.running;
  const langUnsupported = status?.statusCode === 'unsupported_language';

  // El aviso de idioma se rearma cuando el cliente deja de reportarlo
  useEffect(() => {
    if (!langUnsupported) setLangWarnDismissed(false);
  }, [langUnsupported]);
  // Fuente única de verdad (p6): mismo valor que la pill de la cabecera.
  const connected = useClientConnected();

  // Siembra única de módulos/ajustes desde el servidor (después manda el usuario).
  useEffect(() => {
    if (!synced && status) {
      setModules(status.modules);
      setSettings(status.settings);
      setSynced(true);
    }
  }, [synced, status]);

  const saveMutation = useMutation({
    mutationFn: async (payload: { modules: Record<ModuleKey, boolean>; settings: BotSettings }) => {
      await api.saveModules(payload.modules, instanceId);
      await api.saveSettings(payload.settings, instanceId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['botStatus'] });
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 2500);
    }
  });

  // Auto-guardado con debounce: cualquier cambio de módulos/ajustes persiste solo.
  function scheduleSave(nextModules: Record<ModuleKey, boolean>, nextSettings: BotSettings) {
    dirty.current = true;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      dirty.current = false;
      saveMutation.mutate({ modules: nextModules, settings: nextSettings });
    }, 700);
  }

  const setDesiredMutation = useMutation({
    mutationFn: (payload: { desired: 'run' | 'stop'; modules: Record<ModuleKey, boolean> }) =>
      api.setDesired(payload.desired, payload.modules, instanceId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['botStatus'] })
  });

  // -- captura bajo demanda (p5) ------------------------------------------------
  const captureMutation = useMutation({
    mutationFn: () => api.requestCapture(instanceId),
    onError: () => setCaptureReq(null)
  });

  function refreshCapture() {
    if (captureReq?.phase === 'pending') return;
    setCaptureReq({ startedAt: Date.now(), prevCaptureAt: status?.captureAt ?? null, phase: 'pending' });
    captureMutation.mutate();
  }

  useEffect(() => {
    if (!captureReq || captureReq.phase !== 'pending') return;
    if ((status?.captureAt ?? null) !== captureReq.prevCaptureAt) {
      setCaptureReq(null); // llegó la captura fresca
      return;
    }
    if (Date.now() - captureReq.startedAt > CAPTURE_TIMEOUT_MS) {
      setCaptureReq({ ...captureReq, phase: 'timeout' });
    }
  }, [captureReq, status?.captureAt, statusQuery.dataUpdatedAt]);

  function modulesForAction(key: ActionKey): Record<ModuleKey, boolean> {
    if (key === 'auto_bot' || key === 'stop') return modules;
    return { collect: key === 'collect', train_troops: key === 'train_troops', auto_hunt: key === 'auto_hunt' };
  }

  function dispatchAction(key: ActionKey) {
    const label = t(actions.find((a) => a.key === key)!.labelKey);
    const kind = key === 'stop' ? 'stop' : 'start';
    setDesiredMutation.mutate({ desired: key === 'stop' ? 'stop' : 'run', modules: modulesForAction(key) });
    setOperation({ kind, actionLabel: label, phase: 'working', startedAt: Date.now() });
  }

  function sendAction(key: ActionKey) {
    if (operation && operation.phase === 'working') return;
    if (!connected) {
      const label = t(actions.find((a) => a.key === key)!.labelKey);
      setOperation({
        kind: key === 'stop' ? 'stop' : 'start',
        actionLabel: label,
        phase: 'confirm',
        startedAt: Date.now(),
        pendingAction: key
      });
      return;
    }
    dispatchAction(key);
  }

  // Confirmación de la operación observando el estado que reporta el cliente.
  useEffect(() => {
    if (!operation || operation.phase !== 'working') return;
    const confirmed = operation.kind === 'stop' ? !running && !!status : running;
    if (confirmed && Date.now() - operation.startedAt > 1500) {
      setOperation({ ...operation, phase: 'success' });
      return;
    }
    if (Date.now() - operation.startedAt > OPERATION_TIMEOUT_MS) {
      setOperation({ ...operation, phase: 'timeout' });
    }
  }, [operation, running, status, statusQuery.dataUpdatedAt]);

  const captureSrc = captureSource(status);

  return (
    <>
      {/* Flota (0.6): volver a la lista de bots. FUERA del grid y sticky
          (dueño 2026-08-02): dentro del grid, sticky solo puede moverse en su
          propia fila (área de grid) y no "pega"; aquí queda pegado bajo la
          topbar aunque se haga scroll. */}
      {onBack && (
        <div className="fleet-backbar">
          <button type="button" className="fleet-back" onClick={onBack}>
            {t('fleet.back')}
          </button>
        </div>
      )}
    <main className="dashboard-grid">
      {tutorialOpen && (
        <FirstStepsModal
          onClose={(dontShowAgain) => {
            if (dontShowAgain) localStorage.setItem(TUTORIAL_KEY, '1');
            setTutorialOpen(false);
          }}
        />
      )}

      {/* Idioma del juego no soportado (0.5.8): el cliente lo reporta por
          status_code y aquí se exige cerrar el modal (estilo OperationModal).
          Si el código desaparece (usuario ya cambió el idioma) se rearma. */}
      {langUnsupported && !langWarnDismissed && (
        <OperationModal
          active={false}
          tone="warning"
          titleId="lang-warn-title"
          eyebrow={t('langWarn.eyebrow')}
          title={t('langWarn.title')}
          body={t('langWarn.body')}
          actions={[{ label: t('langWarn.close'), onClick: () => setLangWarnDismissed(true), primary: true }]}
        />
      )}

      {operation && (
        <OperationModal
          active={operation.phase === 'working'}
          tone={operationTone(operation)}
          titleId="operation-title"
          eyebrow={operationEyebrow(operation, t)}
          title={operationTitle(operation, t)}
          body={operationBody(operation, t)}
          statusItems={[
            { label: t('dashboard.state'), value: stateLabel(running ? status?.state || 'running' : 'idle', t) },
            { label: t('dashboard.activity'), value: describeStatus(status, t) }
          ]}
          actions={operationActions(operation, t, {
            onCancel: () => setOperation(null),
            onClose: () => setOperation(null),
            onSendAnyway: () => operation.pendingAction && dispatchAction(operation.pendingAction)
          })}
        />
      )}

      <div className="left-stack">
        <Panel title={t('dashboard.actions')} right={<StatusPill kind={running ? 'ok' : ''}>{stateLabel(running ? status?.state || 'running' : 'idle', t)}</StatusPill>}>
          <div className="status-grid">
            <Metric label={t('dashboard.activity')}>{describeStatus(status, t)}</Metric>
            <Metric label={t('dashboard.lastReport')}>{formatRelative(status?.lastReportAt, t)}</Metric>
            {/* "Versión del cliente" → topbar (0.4 p3); "PC conectado" fuera
                (0.4.1): duplicaba la pill de la cabecera. */}
          </div>
          {!connected && <div className="notice warning">{t('dashboard.disconnectedNotice')}</div>}
          <div className="action-list">
            {actions.map((action) => (
              <button
                key={action.key}
                className={action.tone}
                disabled={
                  setDesiredMutation.isPending ||
                  (operation ? operation.phase === 'working' : false) ||
                  // Paridad hosted: con el bot corriendo — o con la orden 'run'
                  // ya enviada y pendiente de que el cliente la confirme
                  // (0.4.1: evita relanzar Auto Bot varias veces) — solo Parar.
                  ((running || status?.desired === 'run') && action.key !== 'stop')
                }
                onClick={() => sendAction(action.key)}
              >
                {t(action.labelKey)}
              </button>
            ))}
          </div>
          {/* Espacio SIEMPRE reservado: el aviso aparece/desaparece por opacidad
              para no empujar el panel de ajustes de abajo (micro-desplazamientos
              al marcar checks — dueño 2026-07-27). */}
          <div className={`settings-saved-flash${savedFlash ? ' is-visible' : ''}`} aria-live="polite">
            {t('dashboard.settingsSaved')}
          </div>
        </Panel>

        <ActionSettingsPanel
          api={api}
          modules={modules}
          settings={settings}
          onModulesChange={(next) => {
            setModules(next);
            scheduleSave(next, settings);
          }}
          onSettingsChange={(next) => {
            setSettings(next);
            scheduleSave(modules, next);
          }}
        />
        {/* Como en el hosted: sección de acordeón tras los módulos, ANTES de Captura.
            0.6.4: recibe la región del mapeo para pintar su progreso y saldo.
            0.6.16: sin región configurada, el cliente AUTODETECTA la de la
            cuenta vinculada y la reporta en statusParams.region — fallback. */}
        <IntelligencePanel
          api={api}
          mappingEnabled={!!settings.extras.intelligence}
          mappingRegionId={
            settings.intel.region_id
            ?? (status?.statusParams?.region != null ? String(status.statusParams.region) : null)
          }
          settings={settings}
          onSettingsChange={(next) => {
            setSettings(next);
            scheduleSave(modules, next);
          }}
          statusCode={status?.statusCode ?? null}
          statusParams={status?.statusParams ?? null}
        />
      </div>

      <div className="center-stack">
        <Panel
          title={t('dashboard.screenshot')}
          right={
            <button
              type="button"
              onClick={refreshCapture}
              disabled={captureReq?.phase === 'pending' || captureMutation.isPending || !connected}
              title={!connected ? t('pills.disconnected') : undefined}
            >
              {captureReq?.phase === 'pending' ? t('dashboard.refreshCapturePending') : `↻ ${t('dashboard.refreshCapture')}`}
            </button>
          }
        >
          <ScreenshotFrame src={captureSrc} state={status?.state || 'idle'} mockLabel={t('dashboard.mockView')} />
          {captureReq?.phase === 'timeout' && <div className="notice warning">{t('dashboard.refreshCaptureTimeout')}</div>}
        </Panel>
      </div>

      <div className="right-stack">
        <SnapshotPanel title={t('dashboard.marches')} rows={status?.snapshots?.marches} />
        <SnapshotPanel title={t('dashboard.training')} rows={status?.snapshots?.training} />
        {/* La descarga del cliente se movió a la vista de FLOTA (2026-07-27):
            un usuario nuevo sin bots no llegaba a este Dashboard por-bot. */}
      </div>
    </main>
    </>
  );
}

// -- helpers ------------------------------------------------------------------

function captureSource(status: BotStatus | undefined): string | null {
  if (!status?.captureB64) return null;
  if (status.captureAt) {
    const at = Date.parse(status.captureAt);
    if (Number.isFinite(at) && Date.now() - at > CAPTURE_FRESH_MS) return null;
  }
  return `data:image/jpeg;base64,${status.captureB64}`;
}

function operationTone(operation: Operation): OperationModalTone {
  switch (operation.phase) {
    case 'confirm': return 'warning';
    case 'working': return 'working';
    case 'success': return 'success';
    case 'timeout': return 'error';
  }
}

type Translate = ReturnType<typeof useI18n>['t'];

function operationEyebrow(operation: Operation, t: Translate): string {
  switch (operation.phase) {
    case 'confirm': return t('op.warningEyebrow');
    case 'working': return t('op.workingEyebrow');
    case 'success': return t('op.successEyebrow');
    case 'timeout': return t('op.errorEyebrow');
  }
}

function operationTitle(operation: Operation, t: Translate): string {
  if (operation.phase === 'confirm') return t('op.disconnectedTitle');
  if (operation.phase === 'timeout') return t('op.timeoutTitle');
  if (operation.phase === 'success') {
    return operation.kind === 'stop' ? t('op.successStopTitle') : t('op.successStartTitle');
  }
  return operation.kind === 'stop' ? t('op.stopTitle') : t('op.startTitle', { action: operation.actionLabel });
}

function operationBody(operation: Operation, t: Translate): string {
  if (operation.phase === 'confirm') return t('op.disconnectedBody');
  if (operation.phase === 'timeout') return t('op.timeoutBody');
  if (operation.phase === 'success') {
    return operation.kind === 'stop' ? t('op.successStopBody') : t('op.successStartBody');
  }
  return operation.kind === 'stop' ? t('op.stopBody') : t('op.startBody');
}

function operationActions(
  operation: Operation,
  t: Translate,
  handlers: { onCancel: () => void; onClose: () => void; onSendAnyway: () => void }
) {
  if (operation.phase === 'confirm') {
    return [
      { label: t('op.cancel'), onClick: handlers.onCancel },
      { label: t('op.sendAnyway'), onClick: handlers.onSendAnyway, primary: true }
    ];
  }
  if (operation.phase === 'working') {
    return [{ label: t('op.cancel'), onClick: handlers.onCancel }];
  }
  return [{ label: t('op.close'), onClick: handlers.onClose, primary: true }];
}

function ScreenshotFrame({ src, state, mockLabel }: { src: string | null; state: string; mockLabel: string }) {
  const { t } = useI18n();
  if (src) {
    return (
      <div className="screenshot-wrap">
        <img className="screenshot-frame" src={src} alt={t('dashboard.screenshot')} />
      </div>
    );
  }
  return (
    <div className="screenshot-wrap">
      <div className="mock-screenshot" aria-label={mockLabel}>
        <div className="mock-game-top">
          <span>WHITE BOT</span>
          <strong>{state}</strong>
        </div>
        <div className="mock-game-map">
          <div className="mock-base" />
          <div className="mock-path path-a" />
          <div className="mock-path path-b" />
        </div>
        <div className="mock-game-bottom">{mockLabel}</div>
      </div>
    </div>
  );
}

function SnapshotPanel({ title, rows }: { title: string; rows: QueueSnapshotRow[] | undefined | null }) {
  const { t } = useI18n();
  const items = rows ?? [];
  return (
    <Panel title={title}>
      {!items.length && <EmptyState>{t('dashboard.noRecentVerification')}</EmptyState>}
      {items.map((row) => (
        <div className="queue-row" key={`${title}-${row.queue}-${row.unit ?? ''}`}>
          <strong>
            {t('dashboard.queue')} {row.queue}
            {row.unit ? ` · ${row.unit}` : ''} — {row.status}
          </strong>
          <span>{row.remaining || '-'}</span>
        </div>
      ))}
    </Panel>
  );
}

function FirstStepsModal({ onClose }: { onClose: (dontShowAgain: boolean) => void }) {
  const { t } = useI18n();
  const [dontShow, setDontShow] = useState(false);
  return (
    <div className="modal-backdrop" role="presentation">
      <div className="tutorial-modal" role="dialog" aria-modal="true" aria-labelledby="tutorial-title">
        <div className="tutorial-modal-header">
          <h2 id="tutorial-title">{t('tutorial.title')}</h2>
          <button type="button" className="icon-button" aria-label={t('tutorial.notNow')} onClick={() => onClose(dontShow)}>
            x
          </button>
        </div>
        <p>{t('tutorial.intro')}</p>
        <div className="tutorial-step-list">
          <section className="tutorial-step">
            <strong>{t('tutorial.step1Title')}</strong>
            <span>{t('tutorial.step1Body')}</span>
          </section>
          <section className="tutorial-step">
            <strong>{t('tutorial.step2Title')}</strong>
            <span>{t('tutorial.step2Body')}</span>
          </section>
          <section className="tutorial-step">
            <strong>{t('tutorial.step3Title')}</strong>
            <span>{t('tutorial.step3Body')}</span>
          </section>
        </div>
        <label className="check tutorial-check">
          <input type="checkbox" checked={dontShow} onChange={(event) => setDontShow(event.target.checked)} />
          {t('tutorial.dontShowAgain')}
        </label>
        <div className="tutorial-actions">
          <button type="button" onClick={() => onClose(dontShow)}>
            {t('tutorial.notNow')}
          </button>
          <button type="button" className="primary" onClick={() => onClose(true)}>
            {t('tutorial.gotIt')}
          </button>
        </div>
      </div>
    </div>
  );
}
